import { confirmDiscard } from '@/stores/confirm';
import { hasUnsavedWork } from '@/stores/unsaved';
import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { formatDate, formatMoney, formatQuantity, formatRate } from '@/format';
import { useTrackUnsaved } from '@/stores/unsaved';
import { UnsavedFormContext, useGuardedCancel } from './unsavedForm';
import { AppIcon, statusKind, type IconKind } from './AppIcon';
import { useUrduPhonetic } from '@/urduPhonetic';
import { useDraggable } from '@/hooks/useDraggable';

/**
 * Shared building blocks for the data screens.
 *
 * Everything sizes from the accessibility tokens rather than fixed padding, so
 * a change in Settings reaches every screen without any of them knowing.
 */

/** Company currency. Returns a dash when the value is absent, which is what a masked cost is. */
export function money(value: string | undefined | null): string {
  return formatMoney(value);
}

/** A price for one unit (cost per kg, average cost, daily rate), shown to the 4 places it is kept to. */
export function rate(value: string | undefined | null): string {
  return formatRate(value);
}

/** Renders at the unit's own precision, so litres read 1.20 and bags read 12. */
export function quantity(value: string, precision = 2): string {
  return formatQuantity(value, precision);
}

/** A typed quantity to the places it really has: 5.00 rather than 5.0000. */
export function lineQuantity(qty: string): string {
  const places = (qty.split('.')[1] ?? '').replace(/0+$/, '').length;
  return quantity(qty, Math.max(2, places));
}

/** In the company's date format (Settings › Company). */
export function shortDate(value: string | Date | null | undefined): string {
  return formatDate(value);
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <p className="errornote" role="alert">
      <span aria-hidden="true">⚠ </span>
      {message}
    </p>
  );
}

export function OkNote({ message }: { message: string }) {
  return (
    <p className="settings__ok" role="status">
      <span aria-hidden="true">✓ </span>
      {message}
    </p>
  );
}

/** Document lifecycle, conveyed by text as well as colour. */
export function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'POSTED' || status === 'APPROVED' || status === 'FULFILLED' || status === 'SENT'
      ? 'pill--ok'
      : status === 'CANCELLED' || status === 'REJECTED' || status === 'REVERSED' || status === 'REFUSED'
        ? 'pill--alert'
        : status === 'PENDING' || status === 'DRAFT' || status === 'WAITING' || status === 'NOT_SENT'
          ? 'pill--warn'
          : '';

  return (
    <span className={`pill ${tone}`}>
      <AppIcon kind={statusKind(status)} size={12} />
      {/* Saved on this PC, not with the server yet (.docs/023). */}
      {status === 'NOT_SENT' ? 'Waiting to send' : status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

export function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string | undefined;
  // exactOptionalPropertyTypes is on, so an optional prop that may be passed
  // as undefined has to say so.
  tone?: 'alert' | 'warn' | undefined;
}) {
  return (
    <div className={`tile ${tone ? `tile--${tone}` : ''}`}>
      <div className="tile__label">{label}</div>
      <div className="tile__value">{value}</div>
      {note && <div className="tile__note">{note}</div>}
    </div>
  );
}

/** Prev/Next paging for a `{ total, items }` list response. Renders nothing
 *  when everything already fits on one page. */
export function Pager({
  skip,
  take,
  total,
  onChange,
}: {
  skip: number;
  take: number;
  total: number;
  onChange: (skip: number) => void;
}) {
  if (total <= take && skip === 0) return null;

  const from = skip + 1;
  const to = Math.min(skip + take, total);

  return (
    <div className="pager">
      <button
        type="button"
        className="btn"
        disabled={skip === 0}
        onClick={() => onChange(Math.max(0, skip - take))}
      >
        ← Previous
      </button>
      <span className="muted">{total === 0 ? 'No rows' : `${from}–${to} of ${total}`}</span>
      <button
        type="button"
        className="btn"
        disabled={to >= total}
        onClick={() => onChange(skip + take)}
      >
        Next →
      </button>
    </div>
  );
}

export function Table({
  head,
  children,
  count,
  empty,
}: {
  head: ReactNode;
  children: ReactNode;
  count: number;
  empty: string;
}) {
  if (count === 0) return <p className="muted">{empty}</p>;

  // Tables are the one thing allowed to scroll sideways; the page never does.
  return (
    <div className="tablewrap">
      <table className="table">
        <thead>{head}</thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function SearchBar({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="searchbar" data-unsaved-ignore>
      <span aria-hidden="true">⌕</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </div>
  );
}

/** How many grid columns a field occupies inside a `formgrid--N`. */
export type FieldSpan = 1 | 2 | 'full';

function spanClass(span: FieldSpan | undefined): string {
  if (span === 2) return ' formgrid__span-2';
  if (span === 'full') return ' formgrid__full';
  return '';
}

export function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required,
  mono,
  urdu,
  hint,
  span,
  disabled,
  min,
  max,
  maxLength,
  numeric,
  suggestions,
  invalid,
  prefix,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  mono?: boolean;
  /** Urdu field: RTL + `lang="ur"`, and typing goes through the Urdu phonetic keyboard (a → ا, Shift+T → ٹ, …). */
  urdu?: boolean;
  hint?: ReactNode;
  span?: FieldSpan;
  disabled?: boolean;
  /** A date field's bounds are date strings, a number field's are numbers. */
  min?: number | string;
  max?: number | string;
  maxLength?: number;
  /** Digits-only text (a code, not a quantity): numeric keypad on phones. */
  numeric?: boolean;
  /** Values already in use, offered as a dropdown; anything else may still be typed. */
  suggestions?: string[];
  /** The value is refused (e.g. already in use): outlines the box, and the hint should say why. */
  invalid?: boolean;
  /** Fixed text shown inside the box before what is typed (e.g. "BIN"); it cannot be typed over. */
  prefix?: string | undefined;
}) {
  const urduTyping = useUrduPhonetic<HTMLInputElement>(value, onChange);
  const listId = useId();
  const hasSuggestions = !!suggestions?.length && !disabled;
  const input = (
      <input
        className={`field__input ${mono ? 'field__input--mono' : ''}${urdu ? ' urdu' : ''}`}
        type={type}
        value={value}
        onChange={urdu ? urduTyping.onChange : (e) => onChange(e.target.value)}
        placeholder={placeholder ?? ''}
        required={required ?? false}
        disabled={disabled}
        min={min}
        max={max}
        maxLength={maxLength}
        {...(invalid ? { 'aria-invalid': true } : {})}
        {...(numeric ? { inputMode: 'numeric' as const, pattern: '[0-9]*' } : {})}
        {...(hasSuggestions ? { list: listId, autoComplete: 'off' } : {})}
        {...(urdu ? { ref: urduTyping.ref, lang: 'ur', dir: 'rtl' as const } : {})}
        // Numeric fields are decimals, not integers: 1.2 litres is normal.
        {...(type === 'number' ? { step: 'any', inputMode: 'decimal' as const } : {})}
      />
  );
  return (
    <label className={`field${spanClass(span)}`}>
      <span className="field__label">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </span>
      {prefix ? (
        <span className={`field__affix${disabled ? ' field__affix--disabled' : ''}`}>
          <span className={`field__prefix${mono ? ' mono' : ''}`}>{prefix}</span>
          {input}
        </span>
      ) : (
        input
      )}
      {hasSuggestions && (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
      {hint && <span className="settings__hint">{hint}</span>}
    </label>
  );
}

export function TextArea({
  label,
  value,
  onChange,
  placeholder,
  required,
  urdu,
  hint,
  span = 'full',
  rows = 3,
  disabled,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  /** Urdu field: RTL + `lang="ur"`, and typing goes through the Urdu phonetic keyboard (a → ا, Shift+T → ٹ, …). */
  urdu?: boolean;
  hint?: ReactNode;
  span?: FieldSpan;
  rows?: number;
  disabled?: boolean;
  /** As long as the server takes, so nothing typed is cut off later. */
  maxLength?: number | undefined;
}) {
  const urduTyping = useUrduPhonetic<HTMLTextAreaElement>(value, onChange);
  return (
    <label className={`field${spanClass(span)}`}>
      <span className="field__label">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </span>
      <textarea
        className={`field__input${urdu ? ' urdu' : ''}`}
        rows={rows}
        value={value}
        onChange={urdu ? urduTyping.onChange : (e) => onChange(e.target.value)}
        placeholder={placeholder ?? ''}
        required={required ?? false}
        disabled={disabled}
        maxLength={maxLength}
        {...(urdu ? { ref: urduTyping.ref, lang: 'ur', dir: 'rtl' as const } : {})}
      />
      {hint && <span className="settings__hint">{hint}</span>}
    </label>
  );
}

/** Today as YYYY-MM-DD in the browser's local time, for a date input. */
export function todayIso(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * The panel an inline add or edit form sits in, above its list. Scrolls
 * itself into view when it opens, so choosing "Edit" on a row further down
 * the page never leaves the form off-screen.
 */
export function FormPanel({
  title,
  intro,
  icon,
  children,
}: {
  title: string;
  intro?: ReactNode;
  /** Decorative, drawn before the title. */
  icon?: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const formId = useTrackUnsaved(ref);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, []);

  return (
    <UnsavedFormContext.Provider value={formId}>
      <section className="panel" ref={ref} aria-label={title}>
        <div className="formpanel__head">
          <div>
            <h2 className={icon ? 'withicon' : undefined}>
              {icon}
              {title}
            </h2>
            {intro && <p className="muted formgroup__hint">{intro}</p>}
          </div>
        </div>
        {children}
      </section>
    </UnsavedFormContext.Provider>
  );
}

/** A titled group of fields: "Supplier & delivery", "Payment terms". */
export function FormGroup({
  title,
  hint,
  columns,
  template,
  card,
  children,
}: {
  title: string;
  hint?: ReactNode;
  /** Lays the children out in a fixed-column grid; omit for free layout. */
  columns?: 2 | 3 | 4;
  /**
   * A `grid-template-columns` value for a row whose fields are not all the
   * same size — e.g. a wide "Supplier" search box beside a narrow "Date".
   * Takes precedence over `columns` when both are given.
   */
  template?: string;
  /** Draws the group as a bordered card instead of a divider-separated block. */
  card?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={`formgroup${card ? ' formgroup--card' : ''}`} aria-label={title}>
      <div>
        <h3 className="formgroup__title">{title}</h3>
        {hint && <p className="formgroup__hint">{hint}</p>}
      </div>
      {template ? (
        <div className="formgrid formgrid--weighted" style={{ '--fg-template': template } as CSSProperties}>
          {children}
        </div>
      ) : columns ? (
        <div className={`formgrid formgrid--${columns}`}>{children}</div>
      ) : (
        children
      )}
    </section>
  );
}

/**
 * A form's footer: Cancel on the start side, committing actions on the end
 * side — never a bare "Done" that leaves the user guessing whether it saves.
 */
export function FormActions({
  onCancel,
  cancelLabel = 'Cancel',
  children,
}: {
  onCancel: () => void;
  cancelLabel?: string;
  children: ReactNode;
}) {
  const cancel = useGuardedCancel(onCancel);
  return (
    <div className="formactions">
      <button type="button" className="btn btn--ghost" onClick={cancel}>
        {cancelLabel}
      </button>
      <span className="formactions__spacer" />
      {children}
    </div>
  );
}

/**
 * A dropdown. `placeholder` names a real "nothing" choice — "All", "None",
 * "Any farm" — offered as an empty-valued row; leave it out (or pass "") when
 * there is no such choice. `prompt` is only what the box says while nothing
 * in the list is picked ("Choose…"); it is never offered as a row, so a
 * setting cannot be picked back to nothing.
 */
export function Select({
  label,
  value,
  onChange,
  options,
  required,
  placeholder,
  prompt,
  hint,
  span,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  required?: boolean;
  placeholder?: string | undefined;
  prompt?: string | undefined;
  hint?: ReactNode;
  span?: FieldSpan;
  disabled?: boolean;
}) {
  // A list that carries its own empty-valued option ("All") needs no second one.
  const nothingRow = !!placeholder && !options.some((o) => o.value === '');
  // A value the list does not hold — still loading, an archived record, or
  // nothing picked yet — would otherwise show as the first option, and
  // picking that option would then change nothing.
  const unmatched = !(nothingRow && value === '') && !options.some((o) => o.value === value);
  return (
    <label className={`field${spanClass(span)}`}>
      <span className="field__label">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </span>
      <select
        className="field__input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required ?? false}
        disabled={disabled}
      >
        {nothingRow && <option value="">{placeholder}</option>}
        {unmatched && (
          <option value={value} disabled hidden>
            {prompt ?? 'Choose…'}
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint && <span className="settings__hint">{hint}</span>}
    </label>
  );
}

/** A dialog can be picked up anywhere but its controls, and its tables,
 *  whose rows may be clickable and whose figures people select to copy. */
const MODAL_DRAG = { handle: '.modal', ignore: 'table' };

/**
 * A modal dialog: traps focus, closes on Escape, and returns focus to
 * whatever was focused before it opened. Built by hand rather than the
 * native `<dialog>` element so behaviour is identical everywhere this runs,
 * including in tests.
 *
 * It can be dragged to uncover what is behind it, and stays wherever it is
 * left until it closes; the grip strip along its top shows that it moves.
 * A click on the backdrop closes it only when the press also started there,
 * so letting go of a drag outside the dialog does not throw the form away.
 */
export function Modal({
  title,
  onClose,
  children,
  wide = false,
  medium = false,
  footer,
  className = '',
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** For tabular content such as a stock card. */
  wide?: boolean;
  /** For a form with several fields side by side, e.g. adding an item —
   *  wider than the default so a multi-column field grid does not collapse
   *  to one column, narrower than `wide`'s table-sized dialogs. */
  medium?: boolean;
  /** Pinned to the dialog's bottom edge while its body scrolls, for the
   *  buttons of a long form. */
  footer?: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const pressedOnOverlay = useRef(false);
  const { dragging, handleProps } = useDraggable(ref, MODAL_DRAG);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    function focusable(): HTMLElement[] {
      return Array.from(
        ref.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
    }
    focusable()[0]?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const els = focusable();
      if (els.length === 0) return;
      const first = els[0]!;
      const last = els[els.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return (
    <div
      className={`modal__overlay${dragging ? ' modal__overlay--dragging' : ''}`}
      onPointerDown={(e) => {
        pressedOnOverlay.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedOnOverlay.current) onClose();
      }}
    >
      <div
        className={`modal${wide ? ' modal--wide' : ''}${medium ? ' modal--medium' : ''}${footer ? ' modal--footed' : ''}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        {...handleProps}
      >
        <div className="modal__grip" title="Drag to move · double-click to re-centre" aria-hidden="true">
          <AppIcon kind="drag" />
        </div>
        {children}
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>
  );
}

export interface DialogSubjectTag {
  icon?: IconKind;
  label: ReactNode;
}

/**
 * The one record a dialog acts on — an item in a store, a person, a flock —
 * shown as a card under the dialog's heading, so it is the first thing read
 * rather than small print between the heading and the fields.
 *
 * The figure is the number the action changes or is checked against (stock
 * on hand, birds alive). Tags place the record: store, lot, bin, farm. A
 * falsy tag is skipped, so callers can write `cond && { … }` inline.
 */
export function DialogSubject({
  icon,
  code,
  name,
  nameUr,
  tags = [],
  figure,
  note,
}: {
  icon?: IconKind;
  code?: string | null;
  name: string;
  nameUr?: string | null;
  tags?: Array<DialogSubjectTag | false | null | undefined>;
  figure?: { label: string; value: ReactNode; unit?: string; flag?: ReactNode };
  note?: ReactNode;
}) {
  const shown = tags.filter((t): t is DialogSubjectTag => Boolean(t));
  return (
    <section className="dlgsubject" aria-label={code ? `${code} ${name}` : name}>
      <div className="dlgsubject__main">
        {icon && (
          <span className="dlgsubject__icon">
            <AppIcon kind={icon} size={20} />
          </span>
        )}
        <div className="dlgsubject__id">
          {code && <span className="dlgsubject__code">{code}</span>}
          <strong className="dlgsubject__name">{name}</strong>
          {nameUr && (
            <span className="dlgsubject__nameur urdu" lang="ur" dir="rtl">
              {nameUr}
            </span>
          )}
          {shown.length > 0 && (
            <ul className="dlgsubject__tags">
              {shown.map((t, i) => (
                <li key={i} className="dlgsubject__tag">
                  {t.icon && <AppIcon kind={t.icon} size={13} />}
                  <span>{t.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {figure && (
          <div className="dlgsubject__figure" role="group" aria-label={figure.label}>
            <span className="dlgsubject__label">{figure.label}</span>
            <span className="dlgsubject__value">
              {figure.value}
              {figure.unit && <small> {figure.unit}</small>}
            </span>
            {figure.flag}
          </div>
        )}
      </div>
      {note && <p className="dlgsubject__note">{note}</p>}
    </section>
  );
}

/** A screen with a list and an optional create form above it. */
export function ScreenWithForm({
  canCreate,
  addLabel,
  showForm,
  onToggle,
  form,
  children,
}: {
  canCreate: boolean;
  addLabel: string;
  showForm: boolean;
  onToggle: () => void;
  form: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="stack">
      {canCreate && (
        <div className="toolbar">
          <button
            className="btn btn--primary"
            onClick={() => {
              // This Cancel sits outside the form panel, so it cannot use
              // UnsavedFormContext the way FormActions does — it asks the
              // store directly. Without this it threw typing away in silence.
              if (!showForm || !hasUnsavedWork()) return onToggle();
              void confirmDiscard().then((ok) => ok && onToggle());
            }}
          >
            {showForm ? 'Cancel' : addLabel}
          </button>
        </div>
      )}

      {showForm && canCreate && form}

      {children}
    </div>
  );
}
